import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

class FireflyComplementaryPurchase extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('submit', this.#handleSubmit);
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);

    if (this.dataset.recommendationsPerformed === 'true') {
      this.#finishLoading(this.dataset.hasProducts === 'true');
      return;
    }

    this.#loadRecommendations();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('input', this.#handleInput);
    this.removeEventListener('submit', this.#handleSubmit);
    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  async #loadRecommendations() {
    const { url, productId, sectionId, blockId } = this.dataset;
    if (!url || !productId || !sectionId || !blockId) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const requestUrl = new URL(url, window.location.origin);
      requestUrl.searchParams.set('product_id', productId);
      requestUrl.searchParams.set('section_id', sectionId);
      requestUrl.searchParams.set('intent', 'complementary');

      const response = await fetch(requestUrl, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Complementary products request failed: ${response.status}`);

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const escapedBlockId = window.CSS?.escape ? window.CSS.escape(blockId) : blockId;
      const replacement = html.querySelector(`[data-firefly-complementary-id="${escapedBlockId}"]`);

      if (!(replacement instanceof HTMLElement)) {
        this.#finishLoading(false);
        return;
      }

      this.innerHTML = replacement.innerHTML;
      this.dataset.recommendationsPerformed = 'true';
      this.dataset.hasProducts = replacement.dataset.hasProducts || 'false';
      this.#finishLoading(this.dataset.hasProducts === 'true');
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[firefly-complementary-purchase] Unable to load complementary products', error);
        this.#finishLoading(false);
      }
    } finally {
      this.#abortController = null;
    }
  }

  #finishLoading(hasProducts) {
    this.hidden = !hasProducts;
    if (!hasProducts) return;

    this.#updateGrandTotal();
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-complementary-toggle]');
    if (!(toggle instanceof HTMLButtonElement) || !this.contains(toggle)) return;

    const content = this.querySelector('[data-complementary-content]');
    if (!(content instanceof HTMLElement)) return;

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!isExpanded));
    toggle.classList.toggle('is-collapsed', isExpanded);
    content.hidden = isExpanded;
  };

  #handleSubmit = async (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-complementary-form]')) return;

    event.preventDefault();

    const form = event.target;
    if (form.dataset.submitting === 'true' || !form.reportValidity()) return;

    const rows = Array.from(form.querySelectorAll('[data-complementary-product]')).filter(
      (row) => row instanceof HTMLElement
    );
    const items = [];

    for (const row of rows) {
      if (!this.#isRowAvailable(row)) continue;

      const variantIdInput = row.querySelector('[data-complementary-variant-id]');
      const quantityInput = row.querySelector('input[ref="quantityInput"]');
      const quantitySelector = row.querySelector('quantity-selector-component');
      if (!(variantIdInput instanceof HTMLInputElement) || !(quantityInput instanceof HTMLInputElement)) continue;

      const quantity = Number(quantityInput.value);
      if (!variantIdInput.value || !Number.isFinite(quantity) || quantity <= 0) continue;

      if (quantitySelector && typeof quantitySelector.canAddToCart === 'function') {
        const validation = quantitySelector.canAddToCart();
        if (!validation.canAdd) {
          const message = (this.dataset.quantityErrorMax || 'Maximum quantity is {{ maximum }}.').replace(
            '{{ maximum }}',
            validation.maxQuantity?.toString() || ''
          );
          this.#showError(row, message);
          return;
        }
      }

      items.push({ variantId: variantIdInput.value, quantity });
    }

    if (items.length === 0) return;

    for (const row of rows) this.#clearError(row);
    this.#setSubmitting(form, true);

    const feedbackRow = rows.find((row) => this.#isRowAvailable(row)) || rows[0];
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const sectionIds = this.#getCartSectionIds();
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            id: item.variantId,
            quantity: item.quantity,
          })),
          sections: sectionIds.join(','),
        }),
      });
      const responseData = await response.json();

      if (!response.ok || responseData.status) {
        const message = responseData.description || responseData.message || 'Add to cart failed';
        if (feedbackRow instanceof HTMLElement) this.#showError(feedbackRow, message);
        this.dispatchEvent(
          new CartErrorEvent({
            error: responseData.message || message,
            code: 'INVALID',
            detail: {
              description: responseData.description,
              errors: responseData.errors,
            },
          })
        );

        try {
          const cart = await this.#fetchCart();
          deferredEventPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              didError: true,
              items: cart.items,
              source: 'firefly-complementary-purchase',
              sourceId: form.id,
              itemCount: totalQuantity,
              productId: form.dataset.productId,
              sections: responseData.sections,
            },
          });
          this.#syncCartQuantities(cart);
        } catch (cartError) {
          deferredEventPromise.reject(cartError);
          console.warn('[firefly-complementary-purchase] Unable to refresh cart after rejected add', cartError);
        }
        return;
      }

      try {
        const cart = await this.#fetchCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: false,
            items: cart.items,
            source: 'firefly-complementary-purchase',
            sourceId: form.id,
            itemCount: totalQuantity,
            productId: form.dataset.productId,
            sections: responseData.sections,
          },
        });
        this.#syncCartQuantities(cart);
      } catch (cartError) {
        deferredEventPromise.reject(cartError);
        console.warn('[firefly-complementary-purchase] Products were added, but cart refresh failed', cartError);
      }

      if (feedbackRow instanceof HTMLElement) this.#announceSuccess(feedbackRow);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Network error during add to cart';
      if (feedbackRow instanceof HTMLElement) this.#showError(feedbackRow, message);
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#setSubmitting(form, false);
    }
  };

  #handleChange = (event) => {
    if (!(event.target instanceof Element)) return;

    const variantSelect = event.target.closest('[data-complementary-variant-select]');
    if (variantSelect instanceof HTMLSelectElement && this.contains(variantSelect)) {
      this.#updateVariant(variantSelect);
      return;
    }

    const row = event.target.closest('[data-complementary-product]');
    if (row instanceof HTMLElement && this.contains(row)) this.#updateRowTotal(row);
  };

  #handleInput = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[ref="quantityInput"]')) return;

    const row = event.target.closest('[data-complementary-product]');
    if (row instanceof HTMLElement && this.contains(row)) this.#updateRowTotal(row);
  };

  #handleQuantityUpdate = (event) => {
    if (!(event.target instanceof Element)) return;

    const row = event.target.closest('[data-complementary-product]');
    if (row instanceof HTMLElement && this.contains(row)) {
      event.stopPropagation();
      this.#updateRowTotal(row);
    }
  };

  #handleCartUpdate = (event) => {
    event.promise
      ?.then(async ({ detail }) => {
        const form = this.querySelector('[data-complementary-form]');
        if (form instanceof HTMLFormElement && detail?.sourceId === form.id) return;

        if (Array.isArray(detail?.items)) {
          this.#syncCartQuantities({ items: detail.items });
          return;
        }

        const cart = await this.#fetchCart();
        this.#syncCartQuantities(cart);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[firefly-complementary-purchase] Unable to sync an external cart update', error);
        }
      });
  };

  #updateVariant(select) {
    const row = select.closest('[data-complementary-product]');
    const option = select.selectedOptions[0];
    if (!(row instanceof HTMLElement) || !(option instanceof HTMLOptionElement)) return;

    const variantIdInput = row.querySelector('[data-complementary-variant-id]');
    const quantityInput = row.querySelector('input[ref="quantityInput"]');
    const quantitySelector = row.querySelector('quantity-selector-component');
    const form = row.closest('[data-complementary-form]');
    const addToCart = form?.querySelector('add-to-cart-component');
    const price = row.querySelector('[data-complementary-price]');
    const image = row.querySelector('.firefly-complementary-purchase__image');

    const available = option.dataset.available === 'true';
    const minimum = option.dataset.minimum || '1';
    const maximum = option.dataset.maximum || null;
    const increment = option.dataset.increment || '1';
    const cartQuantity = option.dataset.cartQuantity || '0';

    row.dataset.unitPrice = option.dataset.price || '0';
    row.dataset.available = String(available);

    if (variantIdInput instanceof HTMLInputElement) variantIdInput.value = option.value;

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.dataset.cartQuantity = cartQuantity;
      quantityInput.disabled = !available;
    }

    if (quantitySelector && typeof quantitySelector.updateConstraints === 'function') {
      quantitySelector.dataset.variantId = option.value;
      quantitySelector.serverDisabledMinus = !available;
      quantitySelector.serverDisabledPlus = !available;
      quantitySelector.updateConstraints(minimum, maximum, increment);
    }

    if (price) price.textContent = option.dataset.priceLabel || '';

    const imageUrl = option.dataset.imageUrl;
    if (image instanceof HTMLImageElement && imageUrl) {
      image.src = imageUrl;
      image.removeAttribute('srcset');
    }

    const variantMedia = option.dataset.variantMedia;
    if (addToCart instanceof HTMLElement && variantMedia) {
      addToCart.dataset.productVariantMedia = variantMedia;
    }

    if (form instanceof HTMLFormElement) this.#updateSubmitAvailability(form);
    this.#updateRowTotal(row);
  }

  #getCartSectionIds() {
    const sectionIds = new Set();
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    }
    return Array.from(sectionIds);
  }

  async #fetchCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent && typeof cartItemsComponent.fetchCartData === 'function') {
      try {
        return await cartItemsComponent.fetchCartData();
      } catch (error) {
        console.warn('[firefly-complementary-purchase] Cart component refresh failed; retrying directly', error);
      }
    }

    return this.#fetchCartDirect();
  }

  async #fetchCartDirect() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  #syncCartQuantities(cart) {
    const cartQuantities = new Map();
    for (const item of cart.items || []) {
      const variantId = String(item.variant_id);
      cartQuantities.set(variantId, (cartQuantities.get(variantId) || 0) + Number(item.quantity || 0));
    }

    for (const row of this.querySelectorAll('[data-complementary-product]')) {
      if (!(row instanceof HTMLElement)) continue;

      for (const option of row.querySelectorAll('[data-complementary-variant-select] option')) {
        if (option instanceof HTMLOptionElement) {
          option.dataset.cartQuantity = String(cartQuantities.get(option.value) || 0);
        }
      }

      const variantIdInput = row.querySelector('[data-complementary-variant-id]');
      const quantitySelector = row.querySelector('quantity-selector-component');
      const quantityInput = row.querySelector('input[ref="quantityInput"]');
      if (!(variantIdInput instanceof HTMLInputElement) || !(quantityInput instanceof HTMLInputElement)) continue;

      const cartQuantity = cartQuantities.get(variantIdInput.value) || 0;
      quantityInput.dataset.cartQuantity = String(cartQuantity);
      if (quantitySelector && typeof quantitySelector.setCartQuantity === 'function') {
        quantitySelector.setCartQuantity(cartQuantity);
      } else if (quantitySelector && typeof quantitySelector.updateCartQuantity === 'function') {
        quantitySelector.updateCartQuantity();
      }
    }

    this.#updateGrandTotal();
  }

  #isRowAvailable(row) {
    const select = row.querySelector('[data-complementary-variant-select]');
    if (select instanceof HTMLSelectElement) return select.selectedOptions[0]?.dataset.available === 'true';
    return row.dataset.available === 'true';
  }

  #setSubmitting(form, submitting) {
    const button = form.querySelector('button[type="submit"]');
    form.dataset.submitting = String(submitting);
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = submitting || !this.#hasAvailableItems(form);
    if (submitting) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
  }

  #hasAvailableItems(form) {
    return Array.from(form.querySelectorAll('[data-complementary-product]')).some(
      (row) => row instanceof HTMLElement && this.#isRowAvailable(row)
    );
  }

  #updateSubmitAvailability(form) {
    const addToCart = form.querySelector('add-to-cart-component');
    const buttonText = form.querySelector('.add-to-cart-text__content > span > span');
    const firstRow = form.querySelector('[data-complementary-product]');
    const canAdd = this.#hasAvailableItems(form);

    if (addToCart && typeof addToCart.enable === 'function' && typeof addToCart.disable === 'function') {
      if (canAdd) addToCart.enable();
      else addToCart.disable();
    }

    if (buttonText && firstRow instanceof HTMLElement) {
      buttonText.textContent = canAdd
        ? firstRow.dataset.addText || 'Add to cart'
        : firstRow.dataset.soldOutText || 'Sold out';
    }
  }

  #showError(row, message) {
    const error = row.querySelector('[data-complementary-error]');
    const errorText = row.querySelector('[data-complementary-error-text]');
    const liveRegion = row.querySelector('[data-complementary-live-region]');
    if (error instanceof HTMLElement) error.classList.remove('hidden');
    if (errorText) errorText.textContent = message;
    if (liveRegion) liveRegion.textContent = message;
  }

  #clearError(row) {
    const error = row.querySelector('[data-complementary-error]');
    const errorText = row.querySelector('[data-complementary-error-text]');
    const liveRegion = row.querySelector('[data-complementary-live-region]');
    if (error instanceof HTMLElement) error.classList.add('hidden');
    if (errorText) errorText.textContent = '';
    if (liveRegion) liveRegion.textContent = '';
  }

  #announceSuccess(row) {
    const liveRegion = row.querySelector('[data-complementary-live-region]');
    if (!liveRegion) return;

    liveRegion.textContent = Theme.translations?.added || 'Added to cart';
    window.setTimeout(() => {
      liveRegion.textContent = '';
    }, 5000);
  }

  #updateRowTotal() {
    this.#updateGrandTotal();
  }

  #updateGrandTotal() {
    const total = this.querySelector('[data-complementary-total]');
    if (!total) return;

    let totalAmount = 0;
    for (const row of this.querySelectorAll('[data-complementary-product]')) {
      if (!(row instanceof HTMLElement) || !this.#isRowAvailable(row)) continue;

      const quantityInput = row.querySelector('input[ref="quantityInput"]');
      if (!(quantityInput instanceof HTMLInputElement)) continue;

      const unitPrice = Number(row.dataset.unitPrice || 0);
      const minimum = Number(quantityInput.min || 1);
      const quantity = Math.max(minimum, Number(quantityInput.value || minimum));
      totalAmount += unitPrice * quantity;
    }

    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || '';

    total.textContent = formatMoney(totalAmount, moneyFormat, currency);
  }
}

if (!customElements.get('firefly-complementary-purchase')) {
  customElements.define('firefly-complementary-purchase', FireflyComplementaryPurchase);
}
